import { useSelector } from 'react-redux';
import { Grid } from '@tempmee/react';
import { useGetOfficeReviewByShift } from '../../../../domain/OfficeReviews';
import { OfficeReviewCard } from './OfficeReviewCard';
import { ProfessionalReviewCard } from './ProfessionalReviewCard';
import { toProfessionalReviewCardModel } from './utils';

/** Whatever the job already knows, so an empty card still names its subject. */
const nameFromJob = (user) =>
  user?.office_name || [user?.first_name, user?.last_name].filter(Boolean).join(' ') || '';

/**
 * The Job Detail Rating tab as GOTU-7339 rebuilt it: the two reviews left for
 * one shift, side by side. `RatingDetails` renders this when the GOTU-6266 flag
 * is on; with it off the tab renders `RatingDetails.legacy` instead, which
 * takes both cards off Redux and never calls organization-service.
 *
 * **The two cards read different sources, and they have to.** The office card
 * shows the professional's review *of the office*, which now comes from
 * organization-service by shift id. The professional card shows the office's
 * review *of the professional*, which organization-service does not hold —
 * `location_review` is professional→office only, and nothing in the epic moves
 * the other direction — so it stays on the monolith's `admin/jobs/{id}`
 * payload, already in Redux by the time this mounts.
 *
 * That is why the ticket's "both cards fetch from organization-service" cannot
 * be met as written, and why only one of these cards has a loading state.
 *
 * The shift id comes from `currentJob`, not from `useParams`. `JobDetails`
 * fetches `history.location.state.job_id` in preference to the URL param, so
 * the two can legitimately disagree — and if they did, this card would show a
 * *different shift's* review than the card beside it. `currentJob.id` is by
 * definition the job whose rating slice is on screen.
 */
const RatingDetailsV2 = () => {
  const rating = useSelector((state) => state.jobs.rating);
  const currentJob = useSelector((state) => state.jobs.currentJob);

  const { review, isPending, isError, refetch } = useGetOfficeReviewByShift({
    jobId: currentJob?.id,
  });
  const professionalReview = toProfessionalReviewCardModel(rating);

  return (
    <Grid columns={2} pt={5} gap={4}>
      <OfficeReviewCard
        officeName={nameFromJob(currentJob?.offer_owner)}
        review={review}
        isPending={isPending}
        isError={isError}
        onRetry={refetch}
      />
      <ProfessionalReviewCard
        professionalName={nameFromJob(currentJob?.offer_final_reciever)}
        review={professionalReview}
      />
    </Grid>
  );
};

export { RatingDetailsV2 };
export default RatingDetailsV2;
